import { inject } from '@angular/core';
import type { Route, Routes } from '@angular/router';
import { permissionGuard } from '../../../../core/guards/permission.guard';
import { PermissionService } from '../../../../core/services/permission.service';
import { CLAIMS_ROUTE } from './claims/claims.routes';
import { REPORTS_ROUTE } from './reports/reports.routes';
import { BENEFITS_SECTION_LIST, benefitsLandingUrl } from './shell/benefits-sections';
import { BENEFITS_FULL_PAGE } from './shared/state/benefits-full-page';
import { BenefitsContextStore } from './shared/state/benefits-context.store';
import { BenefitsReferenceStore } from './shared/state/benefits-reference.store';

/**
 * Every Benefits page is re-checked on in-area navigation too — the admin shell's `permissionGuard` runs only when the
 * shell itself is entered (the Tasks D4 / Performance D61 rule). Redirect-only routes are left alone: Angular rejects
 * `canActivate` beside `redirectTo`, and the redirect's target is guarded anyway.
 */
export function withBenefitsPermissionGuard(routes: Routes): Routes {
  return routes.map((route): Route =>
    route.loadComponent || route.component ? { ...route, canActivate: [...(route.canActivate ?? []), permissionGuard] } : route,
  );
}

export const BENEFITS_ROUTE: Route = {
  path: 'benefits',
  // One instance per Benefits visit, shared by every benefits page; reset when the user leaves /benefits.
  providers: [BenefitsContextStore, BenefitsReferenceStore],
  loadComponent: () => import('./shell/benefits-shell.component').then((m) => m.BenefitsShellComponent),
  children: withBenefitsPermissionGuard([
    {
      // Ruling P5: the first tab the caller may open, else /unauthorized.
      path: '',
      pathMatch: 'full',
      redirectTo: () => {
        const permissions = inject(PermissionService);
        return benefitsLandingUrl((anyOf) => permissions.hasAny(anyOf), inject(BENEFITS_SECTION_LIST));
      },
    },
    {
      // D3: list, filters, paging and the lifecycle row actions (writes need Benefits.Plans.Manage).
      path: 'plans',
      loadComponent: () => import('./plans/plans-page.component').then((m) => m.PlansPageComponent),
    },
    {
      // D3: a new plan; saving opens it at its own URL (the admin shell adds pendingChangesGuard to every page).
      path: 'plans/new',
      loadComponent: () => import('./plans/editor/plan-editor-page.component').then((m) => m.PlanEditorPageComponent),
    },
    {
      // Ruling P8: the renewal preview of a draft; the guard also needs the plans-view group (benefitsAreaAlsoRequires).
      path: 'plans/:id/renewal',
      loadComponent: () => import('./plans/renewal/renewal-page.component').then((m) => m.RenewalPageComponent),
    },
    {
      path: 'plans/:id',
      loadComponent: () => import('./plans/editor/plan-editor-page.component').then((m) => m.PlanEditorPageComponent),
    },
    {
      // SP4b D2: roster + Not enrolled; WN-1529: the enrollment is a full page, still a child route so the roster keeps its filters.
      path: 'enrollments',
      loadComponent: () => import('./enrollments/enrollments-page.component').then((m) => m.EnrollmentsPageComponent),
      children: withBenefitsPermissionGuard([
        {
          path: ':id',
          data: { [BENEFITS_FULL_PAGE]: true },
          loadComponent: () => import('./enrollments/detail/enrollment-detail-page.component').then((m) => m.EnrollmentDetailPageComponent),
        },
      ]),
    },
    {
      // SP4b D5: the register; WN-1530: the dependent is a full page, still a child route so the register keeps its filters.
      path: 'dependents',
      loadComponent: () => import('./dependents/dependents-page.component').then((m) => m.DependentsPageComponent),
      children: withBenefitsPermissionGuard([
        {
          path: ':id',
          data: { [BENEFITS_FULL_PAGE]: true },
          loadComponent: () => import('./dependents/detail/dependent-detail-page.component').then((m) => m.DependentDetailPageComponent),
        },
      ]),
    },
    {
      // D2: list, active filter, add / edit / delete (writes need Benefits.Providers.Manage).
      path: 'providers',
      loadComponent: () => import('./providers/providers-page.component').then((m) => m.ProvidersPageComponent),
    },
    {
      // D4: the tenant's Benefits settings (Benefits.Settings.Manage).
      path: 'settings',
      loadComponent: () => import('./settings/benefits-settings-page.component').then((m) => m.BenefitsSettingsPageComponent),
    },
    // SP4c D1: the Claims tab (imports, register, profiles), a nested area with its own segments (rulings Q8, Q9).
    { ...CLAIMS_ROUTE, children: withBenefitsPermissionGuard(CLAIMS_ROUTE.children ?? []) },
    // SP6 D10: the Reports tab, a nested area with its own segments (Claims precedent, rulings Q8/Q9).
    { ...REPORTS_ROUTE, children: withBenefitsPermissionGuard(REPORTS_ROUTE.children ?? []) },
    {
      // SP6 D7: the provider roster (Benefits.Roster.Export alone).
      path: 'roster',
      loadComponent: () => import('./roster/roster-page.component').then((m) => m.RosterPageComponent),
    },
  ]),
};
